/* eslint-disable no-restricted-syntax -- full-bleed video chrome: Reels is a
   black video canvas with a white text/icon overlay throughout this file,
   matching real Instagram Reels UI, not a Mora surface, so
   bg-background/text-foreground don't apply. Scope is the white/black
   overlay chrome only — no hex literal remains in this file, see
   text-platform-instagram-verified below. */
import {
  ChatCircleIcon,
  DotsThreeVerticalIcon,
  HeartIcon,
  MusicNoteIcon,
  PaperPlaneTiltIcon,
  SealCheckIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import { DEFAULT_PREVIEW_BRAND_COLOR, type PlatformPreviewBaseProps, compactNumber } from './types';

import { cn } from '@/lib/utils';

/**
 * Instagram Reel — full-bleed vertical 9:16 viewer replica (2025-2026 Reels
 * UI): right action rail (like, comment, share, more) weighted toward the
 * lower half with the spinning audio disc at its foot; bottom-left identity
 * block (avatar + username + Follow), caption, audio attribution line; a
 * single thin progress line at the extreme bottom edge (NOT the segmented
 * Stories style). Identity lives entirely in the bottom-left block — Reels
 * has no floating top header the way Stories does. Colors are hardcoded
 * (same rule as TikTokVideo/SafeZoneOverlay): this chrome sits ON arbitrary
 * media and must not invert with the app theme.
 */
export const InstagramReel: React.FC<PlatformPreviewBaseProps & { soundCredit?: string }> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  caption,
  imageUrl,
  videoUrl,
  videoPoster,
  focalX,
  focalY,
  zoom,
  engagement,
  isLoading = false,
  soundCredit,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const shares = engagement?.shares;

  return (
    <article
      className={cn('relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black', className)}
      data-platform="instagram-reel"
    >
      <MediaSlot
        fill
        bg="zinc"
        videoFirst
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        videoPoster={videoPoster}
        focalX={focalX}
        focalY={focalY}
        zoom={zoom}
        isLoading={isLoading}
        alt={caption ?? `${brandName} reel`}
        overlay={mediaOverlay}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60 pointer-events-none" />

      <div className="absolute right-2.5 bottom-16 flex flex-col items-center gap-4">
        <RailAction
          icon={<HeartIcon className="size-7" weight="regular" />}
          label={likes == null ? undefined : compactNumber(likes)}
        />
        <RailAction
          icon={<ChatCircleIcon className="size-7" weight="regular" />}
          label={comments == null ? undefined : compactNumber(comments)}
        />
        <RailAction
          icon={<PaperPlaneTiltIcon className="size-7" weight="regular" />}
          label={shares == null ? undefined : compactNumber(shares)}
        />
        <DotsThreeVerticalIcon className="size-5 text-white" weight="bold" aria-hidden />
        {/* Audio disc — album-art crop of the sound's cover, the lowest rail
            element. The brand avatar stands in for the cover art. */}
        <span className="size-6 rounded-md border-2 border-white/80 overflow-hidden" aria-hidden>
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-full rounded-none"
            textClassName="text-micro font-semibold"
          />
        </span>
      </div>

      <div className="absolute left-3 right-16 bottom-4 text-white space-y-2">
        <div className="flex items-center gap-2 min-w-0">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-8 rounded-full border border-white/40 shrink-0"
            textClassName="text-detail font-semibold"
          />
          <p className="text-caption font-semibold truncate">{handle}</p>
          {isVerified ? (
            <SealCheckIcon
              className="size-3.5 shrink-0 text-platform-instagram-verified"
              weight="fill"
              aria-label="Verified"
            />
          ) : null}
          <span className="shrink-0 rounded-md border border-white/70 px-2 py-0.5 text-micro font-semibold">
            Follow
          </span>
        </div>
        {caption ? <p className="text-detail leading-relaxed line-clamp-2">{caption}</p> : null}
        <div className="flex items-center gap-1.5 text-detail">
          <MusicNoteIcon className="size-3 shrink-0" weight="fill" aria-hidden />
          <span className="truncate">{soundCredit ?? `Original audio · ${brandName}`}</span>
        </div>
      </div>

      {/* Single thin playback line at the extreme bottom edge — deliberately
          subtle, unlike Stories' segmented bars.

          🔴 EMPTY, and it must stay empty. This used to render `w-1/3`, which
          claims a third of the reel has played. Nothing has: this is a static
          preview of an asset that has never been watched, and the position was
          chosen to look plausible rather than measured from anything. Same
          rule the paid replicas hold — `TwitterAdPreview`'s playhead and
          `StoryAdPreview`'s progress bar both sit at zero and say why in their
          headers. If a real playback position ever exists here, take it as a
          prop; do not reinstate a constant. */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20" aria-hidden>
        <div className="h-full w-0 bg-white/80" />
      </div>
    </article>
  );
};

const RailAction: React.FC<{ icon: React.ReactNode; label?: string }> = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-0.5 text-white">
    <span aria-hidden>{icon}</span>
    {label != null ? <span className="text-micro font-semibold">{label}</span> : null}
  </div>
);
